import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, getBackendUrl, getUserFacingErrorMessage } from '../services/apiClient';
import { clearAuth } from '../services/authService';
import './GovernancePages.css';

function detailFromErrorPayload(data) {
  if (!data || data.detail === undefined) return 'Request failed';
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === 'object' && x.msg ? x.msg : JSON.stringify(x)))
      .join(' ');
  }
  return String(d);
}

async function readJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function userFacingFromResponse(res, data) {
  return getUserFacingErrorMessage(res.status, data, detailFromErrorPayload(data));
}

/**
 * Platform console — TalentVibe operators: companies + platform admins.
 * Gated on talentvibe_admin from /api/auth/status.
 */
export function PlatformConsolePage() {
  const [allowed, setAllowed] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('companies');

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [listError, setListError] = useState(null);

  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanySlug, setNewCompanySlug] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  const [inviteCompany, setInviteCompany] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState(null);
  const [adminInviteEmail, setAdminInviteEmail] = useState('');
  const [adminInviteBusy, setAdminInviteBusy] = useState(false);
  const [adminInviteError, setAdminInviteError] = useState(null);
  const [adminInviteResult, setAdminInviteResult] = useState(null);

  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const [runtimeError, setRuntimeError] = useState(null);
  const [checklistCompany, setChecklistCompany] = useState(null);

  const [platformUsers, setPlatformUsers] = useState([]);
  const [platformUsersLoading, setPlatformUsersLoading] = useState(false);
  const [platformUserError, setPlatformUserError] = useState(null);
  const [platformUserSearch, setPlatformUserSearch] = useState('');
  const [platformCompanyFilter, setPlatformCompanyFilter] = useState('');
  const [platformUserEdit, setPlatformUserEdit] = useState(null);
  const [platformUserSaving, setPlatformUserSaving] = useState(false);
  const [platformUserDelete, setPlatformUserDelete] = useState(null);
  const [platformUserDeleteEmail, setPlatformUserDeleteEmail] = useState('');
  const [platformUserDeleteBusy, setPlatformUserDeleteBusy] = useState(false);
  const platformUserSearchRef = useRef('');
  const platformCompanyFilterRef = useRef('');
  platformUserSearchRef.current = platformUserSearch;
  platformCompanyFilterRef.current = platformCompanyFilter;

  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [supportStatusFilter, setSupportStatusFilter] = useState('');
  const [supportSelected, setSupportSelected] = useState(null);
  const [supportReply, setSupportReply] = useState('');
  const [supportInternal, setSupportInternal] = useState(false);
  const [supportReplyBusy, setSupportReplyBusy] = useState(false);
  const [supportPatchBusy, setSupportPatchBusy] = useState(false);
  const [supportReplyAck, setSupportReplyAck] = useState(null);
  const [supportPatchAck, setSupportPatchAck] = useState(null);
  const [supportPatchStatus, setSupportPatchStatus] = useState('open');
  const [supportPatchPriority, setSupportPatchPriority] = useState('medium');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch('/api/auth/status');
        const d = await r.json();
        if (cancelled) return;
        const role = d.user?.role;
        const ok =
          d.authenticated &&
          !d.user?.company_id &&
          (role === 'talentvibe_admin' || role === 'talentvibe_support_readonly');
        setAllowed(ok);
        if (ok) setMe(d.user);
        else setGateError('TalentVibe platform administrator access only.');
      } catch (e) {
        if (!cancelled) {
          setAllowed(false);
          setGateError(String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadCompanies = useCallback(async (searchText = '') => {
    setCompaniesLoading(true);
    setListError(null);
    try {
      const qs = new URLSearchParams();
      const t = String(searchText || '').trim();
      if (t) qs.set('q', t);
      qs.set('limit', '200');
      qs.set('enrich', '1');
      const r = await apiFetch(`/api/v1/platform/companies?${qs.toString()}`);
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setListError(userFacingFromResponse(r, data));
        setCompanies([]);
        return;
      }
      setCompanies(data.companies || []);
    } catch (e) {
      setListError(String(e));
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed !== true || tab !== 'companies') return;
    loadCompanies('');
  }, [allowed, tab, loadCompanies]);

  const loadSupportTickets = useCallback(async () => {
    setSupportLoading(true);
    setSupportError(null);
    try {
      const qs = new URLSearchParams();
      if (supportStatusFilter) qs.set('status', supportStatusFilter);
      const r = await apiFetch(`/api/support-tickets/admin?${qs.toString()}`);
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setSupportError(userFacingFromResponse(r, data));
        setSupportTickets([]);
        return;
      }
      setSupportTickets(data.tickets || []);
    } catch (e) {
      setSupportError(String(e));
      setSupportTickets([]);
    } finally {
      setSupportLoading(false);
    }
  }, [supportStatusFilter]);

  useEffect(() => {
    if (allowed !== true || tab !== 'support') return;
    void loadSupportTickets();
  }, [allowed, tab, loadSupportTickets]);

  const openSupportTicket = (t) => {
    setSupportSelected(t);
    setSupportReply('');
    setSupportInternal(false);
    setSupportReplyAck(null);
    setSupportPatchAck(null);
    setSupportPatchStatus(t.status || 'open');
    setSupportPatchPriority(t.priority || 'medium');
  };

  const sendSupportReply = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supportSelected || !supportReply.trim() || supportReplyBusy) return;
    setSupportReplyBusy(true);
    setSupportError(null);
    setSupportReplyAck(null);
    try {
      const ref = supportSelected.public_id || supportSelected.id;
      const r = await apiFetch(`/api/support-tickets/${encodeURIComponent(ref)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: supportReply.trim(),
          visibility: supportInternal ? 'internal' : 'customer',
        }),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setSupportError(userFacingFromResponse(r, data));
        return;
      }
      setSupportSelected(data.ticket);
      setSupportReply('');
      setSupportInternal(false);
      setSupportReplyAck(supportInternal ? 'Internal note saved' : 'Reply sent');
      void loadSupportTickets();
    } catch (err) {
      setSupportError(String(err));
    } finally {
      setSupportReplyBusy(false);
    }
  };

  const patchSupportTicket = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supportSelected || supportPatchBusy) return;
    setSupportPatchBusy(true);
    setSupportError(null);
    setSupportPatchAck(null);
    try {
      const ref = supportSelected.public_id || supportSelected.id;
      const r = await apiFetch(`/api/support-tickets/${encodeURIComponent(ref)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: supportPatchStatus,
          priority: supportPatchPriority,
        }),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setSupportError(userFacingFromResponse(r, data));
        return;
      }
      setSupportSelected(data.ticket);
      setSupportPatchAck('Status updated');
      void loadSupportTickets();
    } catch (err) {
      setSupportError(String(err));
    } finally {
      setSupportPatchBusy(false);
    }
  };

  const loadAdmins = useCallback(async () => {
    setAdminsLoading(true);
    setAdminsError(null);
    try {
      const r = await apiFetch('/api/v1/platform/admins');
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setAdminsError(userFacingFromResponse(r, data));
        setAdmins([]);
        return;
      }
      setAdmins(data.admins || []);
    } catch (e) {
      setAdminsError(String(e));
      setAdmins([]);
    } finally {
      setAdminsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed !== true || tab !== 'admins') return;
    loadAdmins();
  }, [allowed, tab, loadAdmins]);

  const loadRuntimeConfig = useCallback(async () => {
    setRuntimeError(null);
    try {
      const r = await apiFetch('/api/v1/platform/runtime-config');
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setRuntimeError(userFacingFromResponse(r, data));
        setRuntimeConfig(null);
        return;
      }
      setRuntimeConfig(data);
    } catch (e) {
      setRuntimeError(String(e));
      setRuntimeConfig(null);
    }
  }, []);

  useEffect(() => {
    if (allowed !== true || tab !== 'policies') return;
    loadRuntimeConfig();
  }, [allowed, tab, loadRuntimeConfig]);

  const loadPlatformUsers = useCallback(async () => {
    setPlatformUsersLoading(true);
    setPlatformUserError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '500');
      const t = String(platformUserSearchRef.current || '').trim();
      if (t) qs.set('q', t);
      const c = String(platformCompanyFilterRef.current || '').trim();
      if (c) qs.set('company_id', c);
      const r = await apiFetch(`/api/v1/platform/users?${qs.toString()}`);
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setPlatformUserError(userFacingFromResponse(r, data));
        setPlatformUsers([]);
        return;
      }
      setPlatformUsers(data.users || []);
    } catch (e) {
      setPlatformUserError(String(e));
      setPlatformUsers([]);
    } finally {
      setPlatformUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed !== true || tab !== 'users') return;
    void loadPlatformUsers();
  }, [allowed, tab, loadPlatformUsers]);

  const createCompany = async (e) => {
    e.preventDefault();
    setCreateBusy(true);
    setCreateMsg(null);
    try {
      const body = { name: newCompanyName.trim() };
      const slug = newCompanySlug.trim();
      if (slug) body.slug = slug;
      const r = await apiFetch('/api/v1/platform/companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setCreateMsg({ type: 'error', text: userFacingFromResponse(r, data) });
        return;
      }
      setCreateMsg({ type: 'ok', text: `Created tenant “${data.name}” (id ${data.id}, slug ${data.slug}).` });
      setNewCompanyName('');
      setNewCompanySlug('');
      loadCompanies('');
    } catch (err) {
      setCreateMsg({ type: 'error', text: String(err) });
    } finally {
      setCreateBusy(false);
    }
  };

  const submitFirstAdminInvite = async (e) => {
    e.preventDefault();
    if (!inviteCompany) return;
    setInviteBusy(true);
    setInviteError(null);
    setInviteResult(null);
    try {
      const r = await apiFetch(`/api/v1/platform/companies/${inviteCompany.id}/invite-first-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setInviteError(userFacingFromResponse(r, data));
        return;
      }
      if (data.status === 'already_invited') {
        setInviteResult({ already: true, company_id: data.company_id });
        return;
      }
      setInviteResult(data);
    } catch (err) {
      setInviteError(String(err));
    } finally {
      setInviteBusy(false);
    }
  };

  const closeInviteModal = () => {
    setInviteCompany(null);
    setInviteEmail('');
    setInviteError(null);
    setInviteResult(null);
  };

  const submitPlatformAdminInvite = async (e) => {
    e.preventDefault();
    setAdminInviteBusy(true);
    setAdminInviteError(null);
    setAdminInviteResult(null);
    try {
      const r = await apiFetch('/api/v1/platform/admins/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminInviteEmail.trim() }),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        setAdminInviteError(userFacingFromResponse(r, data));
        return;
      }
      setAdminInviteResult(data);
      setAdminInviteEmail('');
      loadAdmins();
    } catch (err) {
      setAdminInviteError(String(err));
    } finally {
      setAdminInviteBusy(false);
    }
  };

  const deactivateAdmin = async (userId) => {
    if (!window.confirm('Deactivate this platform administrator? They will no longer be able to sign in.')) {
      return;
    }
    try {
      const r = await apiFetch(`/api/v1/platform/admins/${userId}/deactivate`, { method: 'POST' });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        window.alert(userFacingFromResponse(r, data));
        return;
      }
      loadAdmins();
    } catch (err) {
      window.alert(String(err));
    }
  };

  const copyText = (label, text) => {
    navigator.clipboard.writeText(text).then(
      () => window.alert(`${label} copied to clipboard.`),
      () => window.prompt(`Copy ${label}:`, text)
    );
  };

  const openPlatformUserEdit = (row) => {
    setPlatformUserEdit({
      ...row,
      roleEdit: row.role,
      statusEdit: row.membership_status,
      scopeText: JSON.stringify(row.scope && typeof row.scope === 'object' ? row.scope : {}),
    });
  };

  const closePlatformUserEdit = () => setPlatformUserEdit(null);

  const savePlatformUserEdit = async (e) => {
    e.preventDefault();
    if (!platformUserEdit) return;
    setPlatformUserSaving(true);
    try {
      let scope;
      try {
        scope = JSON.parse(platformUserEdit.scopeText || '{}');
        if (typeof scope !== 'object' || scope === null) throw new Error('not an object');
      } catch (err) {
        window.alert('Invalid JSON in scope field.');
        return;
      }
      const r = await apiFetch(
        `/api/v1/platform/companies/${platformUserEdit.company_id}/memberships/${platformUserEdit.membership_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: platformUserEdit.roleEdit,
            status: platformUserEdit.statusEdit,
            scope,
          }),
        }
      );
      const data = await readJsonSafe(r);
      if (!r.ok) {
        window.alert(userFacingFromResponse(r, data));
        return;
      }
      setPlatformUserEdit(null);
      void loadPlatformUsers();
    } catch (err) {
      window.alert(String(err));
    } finally {
      setPlatformUserSaving(false);
    }
  };

  const suspendPlatformUser = async (row) => {
    if (!window.confirm(`Suspend ${row.email} in “${row.company_name}” (id ${row.company_id})?`)) return;
    try {
      const r = await apiFetch(
        `/api/v1/platform/companies/${row.company_id}/memberships/${row.membership_id}`,
        { method: 'DELETE' }
      );
      const data = await readJsonSafe(r);
      if (!r.ok) {
        window.alert(userFacingFromResponse(r, data));
        return;
      }
      void loadPlatformUsers();
    } catch (e) {
      window.alert(String(e));
    }
  };

  const closePlatformUserDelete = () => {
    setPlatformUserDelete(null);
    setPlatformUserDeleteEmail('');
  };

  const submitPlatformUserDelete = async (e) => {
    e.preventDefault();
    if (!platformUserDelete) return;
    if (
      (platformUserDeleteEmail || '').trim().toLowerCase() !==
      (platformUserDelete.email || '').trim().toLowerCase()
    ) {
      window.alert('Type the user’s email exactly to confirm.');
      return;
    }
    if (
      !window.confirm(
        'Permanently delete this user and all jobs they own? This cannot be undone.'
      )
    ) {
      return;
    }
    setPlatformUserDeleteBusy(true);
    try {
      const r = await apiFetch(`/api/v1/platform/users/${platformUserDelete.user_id}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_email: platformUserDeleteEmail.trim() }),
      });
      const data = await readJsonSafe(r);
      if (!r.ok) {
        window.alert(userFacingFromResponse(r, data));
        return;
      }
      closePlatformUserDelete();
      void loadPlatformUsers();
    } catch (err) {
      window.alert(String(err));
    } finally {
      setPlatformUserDeleteBusy(false);
    }
  };

  if (allowed === null) {
    return (
      <div className="governance-page">
        <p className="page-lead">Loading…</p>
      </div>
    );
  }
  if (!allowed) {
    return (
      <div className="governance-page">
        <h1>Platform</h1>
        <p className="page-lead">{gateError || 'Access denied.'}</p>
      </div>
    );
  }

  return (
    <div className="governance-page">
      <h1>Platform console</h1>
      <p className="page-lead">
        Onboard tenants, invite each company&apos;s first administrator, and manage TalentVibe platform
        administrators.
      </p>

      <div className="platform-tabs">
        <button type="button" className={tab === 'companies' ? 'is-active' : ''} onClick={() => setTab('companies')}>
          Companies
        </button>
        <button type="button" className={tab === 'admins' ? 'is-active' : ''} onClick={() => setTab('admins')}>
          Platform admins
        </button>
        <button type="button" className={tab === 'policies' ? 'is-active' : ''} onClick={() => setTab('policies')}>
          Policies
        </button>
        <button type="button" className={tab === 'users' ? 'is-active' : ''} onClick={() => setTab('users')}>
          All users
        </button>
        <button type="button" className={tab === 'support' ? 'is-active' : ''} onClick={() => setTab('support')}>
          Support tickets
        </button>
      </div>

      {tab === 'companies' && (
        <>
          <section className="platform-panel">
            <h2>Create company</h2>
            <form onSubmit={createCompany}>
              <div className="platform-toolbar">
                <input
                  type="text"
                  placeholder="Company name"
                  value={newCompanyName}
                  onChange={(ev) => setNewCompanyName(ev.target.value)}
                  required
                  maxLength={255}
                />
                <input
                  type="text"
                  placeholder="Slug (optional — derived from name if empty)"
                  value={newCompanySlug}
                  onChange={(ev) => setNewCompanySlug(ev.target.value)}
                  maxLength={120}
                />
                <button type="submit" className="platform-btn" disabled={createBusy}>
                  {createBusy ? 'Creating…' : 'Create tenant'}
                </button>
              </div>
            </form>
            {createMsg && (
              <div className={`platform-alert ${createMsg.type === 'error' ? 'platform-alert--error' : 'platform-alert--success'}`}>
                {createMsg.text}
              </div>
            )}
          </section>

          <section className="platform-panel">
            <h2>All companies</h2>
            <div className="platform-toolbar">
              <input
                type="search"
                placeholder="Search by name or slug"
                value={companySearch}
                onChange={(ev) => setCompanySearch(ev.target.value)}
              />
              <button
                type="button"
                className="platform-btn platform-btn--ghost"
                onClick={() => loadCompanies(companySearch)}
                disabled={companiesLoading}
              >
                {companiesLoading ? 'Loading…' : 'Search / refresh'}
              </button>
            </div>
            {listError && <div className="platform-alert platform-alert--error">{listError}</div>}
            <div className="platform-table-wrap">
              <table className="platform-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Active</th>
                    <th>Plan</th>
                    <th>SSO</th>
                    <th>Members</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {companies.length === 0 && !companiesLoading && (
                    <tr>
                      <td colSpan={9} style={{ color: 'var(--text-color-dark)' }}>
                        No companies match this filter.
                      </td>
                    </tr>
                  )}
                  {companies.map((c) => (
                    <tr key={c.id}>
                      <td>{c.id}</td>
                      <td>{c.name}</td>
                      <td>
                        <code>{c.slug || '—'}</code>
                      </td>
                      <td>
                        <span className="platform-badge">{c.tenant_status}</span>
                      </td>
                      <td>{c.is_active ? 'Yes' : 'No'}</td>
                      <td>
                        {c.subscription_tier} / {c.subscription_status}
                      </td>
                      <td>{c.sso_active ? 'On' : '—'}</td>
                      <td>{typeof c.active_member_count === 'number' ? c.active_member_count : '—'}</td>
                      <td>
                        <button type="button" className="platform-btn platform-btn--ghost" onClick={() => setChecklistCompany(c)}>
                          Checklist
                        </button>
                        <button type="button" className="platform-btn platform-btn--ghost" style={{ marginLeft: 6 }} onClick={() => setInviteCompany(c)}>
                          Invite admin
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === 'admins' && (
        <section className="platform-panel">
          <h2>TalentVibe platform administrators</h2>
          <p className="page-lead" style={{ marginTop: '-0.5rem' }}>
            Invite another operator by email. They receive a temporary password here — share it through a secure
            channel. They must change it after first login when enforced.
          </p>
          {adminsError && <div className="platform-alert platform-alert--error">{adminsError}</div>}
          <form onSubmit={submitPlatformAdminInvite}>
            <div className="platform-toolbar">
              <input
                type="email"
                required
                placeholder="colleague@company.com"
                value={adminInviteEmail}
                onChange={(ev) => setAdminInviteEmail(ev.target.value)}
              />
              <button type="submit" className="platform-btn" disabled={adminInviteBusy}>
                {adminInviteBusy ? 'Inviting…' : 'Invite platform admin'}
              </button>
            </div>
          </form>
          {adminInviteError && <div className="platform-alert platform-alert--error">{adminInviteError}</div>}
          {adminInviteResult && (
            <div className="platform-alert platform-alert--success">
              <strong>Invitation created.</strong> User id {adminInviteResult.user_id}. Copy the temporary password
              now — it is not shown again.
              <div className="platform-secret-block">{adminInviteResult.temporary_password}</div>
              <button type="button" className="platform-btn" style={{ marginTop: '0.75rem' }} onClick={() => copyText('Password', adminInviteResult.temporary_password)}>
                Copy password
              </button>
            </div>
          )}

          <h3 style={{ color: '#fff', marginTop: '1.5rem', fontSize: '1.1rem' }}>Current admins</h3>
          {adminsLoading && <p className="page-lead">Loading…</p>}
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.id}>
                    <td>{a.id}</td>
                    <td>{a.email}</td>
                    <td>{a.username}</td>
                    <td>{a.is_active ? 'Yes' : 'No'}</td>
                    <td>
                      {me && a.id !== me.id && a.is_active && (
                        <button type="button" className="platform-btn platform-btn--danger" onClick={() => deactivateAdmin(a.id)}>
                          Deactivate
                        </button>
                      )}
                      {me && a.id === me.id && <span style={{ color: 'var(--text-color-dark)' }}>You</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'policies' && (
        <section className="platform-panel">
          <h2>Runtime policy</h2>
          <p className="page-lead" style={{ marginTop: '-0.5rem' }}>
            Values returned from the API for this deployment. Public registration is controlled by{' '}
            <code>ALLOW_PUBLIC_REGISTRATION</code> (defaults off in production unless set).
          </p>
          {runtimeError && <div className="platform-alert platform-alert--error">{runtimeError}</div>}
          {runtimeConfig && (
            <ul style={{ listStyle: 'none', padding: 0, color: 'var(--text-color)', lineHeight: 1.8 }}>
              <li><strong>Environment:</strong> {String(runtimeConfig.environment)}</li>
              <li>
                <strong>Public registration:</strong>{' '}
                {runtimeConfig.allow_public_registration ? 'enabled' : 'disabled'}
              </li>
            </ul>
          )}
        </section>
      )}

      {tab === 'users' && (
        <section className="platform-panel">
          <h2>Tenant users (company-wide)</h2>
          <p className="page-lead" style={{ marginTop: '-0.5rem' }}>
            All memberships with company and scope. Use filters, then <strong>Search / refresh</strong>. Edit uses JSON
            for scope. <strong>Delete permanently</strong> removes the user account and all jobs they own (irreversible).
          </p>
          {platformUserError && <div className="platform-alert platform-alert--error">{platformUserError}</div>}
          <div className="platform-toolbar">
            <input
              type="search"
              placeholder="Filter by email (contains)"
              value={platformUserSearch}
              onChange={(ev) => setPlatformUserSearch(ev.target.value)}
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="Company ID (optional)"
              value={platformCompanyFilter}
              onChange={(ev) => setPlatformCompanyFilter(ev.target.value)}
            />
            <button type="button" className="platform-btn" onClick={() => void loadPlatformUsers()} disabled={platformUsersLoading}>
              {platformUsersLoading ? 'Loading…' : 'Search / refresh'}
            </button>
          </div>
          {platformUsersLoading && <p className="page-lead">Loading…</p>}
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Role</th>
                  <th>Scope</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {platformUsers.length === 0 && !platformUsersLoading && (
                  <tr>
                    <td colSpan={7} style={{ color: 'var(--text-color-dark)' }}>
                      No users match this filter.
                    </td>
                  </tr>
                )}
                {platformUsers.map((u) => (
                  <tr key={`${u.company_id}-${u.membership_id}`}>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>
                      <span title={`Company id ${u.company_id}`}>{u.company_name}</span>
                    </td>
                    <td>{u.membership_status}</td>
                    <td>{u.role}</td>
                    <td>
                      <code style={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>{JSON.stringify(u.scope || {})}</code>
                    </td>
                    <td>
                      <button type="button" className="platform-btn platform-btn--ghost" onClick={() => openPlatformUserEdit(u)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="platform-btn platform-btn--danger"
                        style={{ marginLeft: 6 }}
                        onClick={() => void suspendPlatformUser(u)}
                      >
                        Suspend
                      </button>
                      <button
                        type="button"
                        className="platform-btn platform-btn--danger"
                        style={{ marginLeft: 6 }}
                        disabled={me && u.user_id === me.id}
                        title={me && u.user_id === me.id ? 'Cannot delete your own account' : ''}
                        onClick={() => {
                          setPlatformUserDelete(u);
                          setPlatformUserDeleteEmail('');
                        }}
                      >
                        Delete permanently
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'support' && (
        <section className="platform-panel">
          <h2>Support tickets</h2>
          <p className="page-lead" style={{ marginTop: '-0.5rem' }}>
            Customer Help &amp; Support tickets. Replies notify the ticket creator; new tickets notify{' '}
            talentvibe@govcomsolutions.com.
          </p>
          {supportError && <div className="platform-alert platform-alert--error">{supportError}</div>}
          <div className="platform-toolbar">
            <select
              value={supportStatusFilter}
              onChange={(ev) => setSupportStatusFilter(ev.target.value)}
              style={{
                padding: '0.55rem 0.75rem',
                borderRadius: '10px',
                border: '1px solid var(--glass-border-color)',
                background: 'rgba(0,0,0,0.25)',
                color: 'var(--text-color)',
              }}
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="waiting_on_customer">Waiting on customer</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <button
              type="button"
              className="platform-btn"
              onClick={() => void loadSupportTickets()}
              disabled={supportLoading}
            >
              {supportLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Subject</th>
                  <th>Company</th>
                  <th>From</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {supportTickets.length === 0 && !supportLoading && (
                  <tr>
                    <td colSpan={7}>No tickets found.</td>
                  </tr>
                )}
                {supportTickets.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <code>{t.public_id}</code>
                    </td>
                    <td>{t.subject}</td>
                    <td>{t.company_name || t.company_id}</td>
                    <td>{t.created_by_email || t.created_by_user_id}</td>
                    <td>{t.status}</td>
                    <td>{t.priority}</td>
                    <td>
                      <button
                        type="button"
                        className="platform-btn platform-btn--ghost"
                        onClick={() => openSupportTicket(t)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {supportSelected && (
            <div
              className="platform-modal-backdrop"
              role="presentation"
              onClick={() => setSupportSelected(null)}
            >
              <div
                className="platform-modal"
                role="dialog"
                aria-modal="true"
                onClick={(ev) => ev.stopPropagation()}
                style={{ maxWidth: '640px', maxHeight: '85vh', overflow: 'auto' }}
              >
                <h3>
                  {supportSelected.public_id} — {supportSelected.subject}
                </h3>
                <p style={{ color: 'var(--text-color-dark)', fontSize: '0.9rem' }}>
                  {supportSelected.company_name} · {supportSelected.created_by_email} ·{' '}
                  {supportSelected.category}
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  {(supportSelected.messages || []).map((m) => (
                    <div
                      key={m.id}
                      style={{
                        marginBottom: '0.75rem',
                        padding: '0.75rem',
                        borderRadius: '10px',
                        border: '1px solid var(--glass-border-color)',
                        background:
                          m.visibility === 'internal'
                            ? 'rgba(253, 230, 138, 0.08)'
                            : 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-color-dark)' }}>
                        {m.visibility === 'internal' ? 'Internal' : 'Customer-visible'} ·{' '}
                        {m.author_label || `user #${m.author_user_id}`}
                        {m.author_name ? ` (${m.author_name})` : ''} ·{' '}
                        {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                      </div>
                      <pre style={{ whiteSpace: 'pre-wrap', margin: '0.35rem 0 0', fontFamily: 'inherit' }}>
                        {m.body}
                      </pre>
                    </div>
                  ))}
                </div>

                {me?.role === 'talentvibe_admin' && (
                  <form onSubmit={patchSupportTicket} style={{ marginBottom: '1rem' }}>
                    <div className="platform-toolbar">
                      <select
                        value={supportPatchStatus}
                        onChange={(ev) => setSupportPatchStatus(ev.target.value)}
                      >
                        <option value="open">open</option>
                        <option value="in_progress">in_progress</option>
                        <option value="waiting_on_customer">waiting_on_customer</option>
                        <option value="resolved">resolved</option>
                        <option value="closed">closed</option>
                      </select>
                      <select
                        value={supportPatchPriority}
                        onChange={(ev) => setSupportPatchPriority(ev.target.value)}
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="urgent">urgent</option>
                      </select>
                      <button type="submit" className="platform-btn" disabled={supportPatchBusy}>
                        {supportPatchBusy ? 'Updating…' : 'Update status'}
                      </button>
                    </div>
                    {supportPatchAck && (
                      <p style={{ margin: '0.5rem 0 0', color: 'var(--primary-color)', fontSize: '0.9rem' }}>
                        {supportPatchAck}
                      </p>
                    )}
                  </form>
                )}

                <form onSubmit={sendSupportReply}>
                  <label className="team-gov__label">
                    <span>Reply</span>
                    <textarea
                      rows={4}
                      value={supportReply}
                      onChange={(ev) => {
                        setSupportReply(ev.target.value);
                        if (supportReplyAck) setSupportReplyAck(null);
                      }}
                      required
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        borderRadius: '10px',
                        border: '1px solid var(--glass-border-color)',
                        background: 'rgba(0,0,0,0.25)',
                        color: 'var(--text-color)',
                        padding: '0.65rem',
                        fontFamily: 'inherit',
                      }}
                    />
                  </label>
                  {me?.role === 'talentvibe_admin' && (
                    <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0' }}>
                      <input
                        type="checkbox"
                        checked={supportInternal}
                        onChange={(ev) => setSupportInternal(ev.target.checked)}
                      />
                      Internal note (not emailed / not shown to customer)
                    </label>
                  )}
                  {supportReplyAck && (
                    <p style={{ margin: '0.5rem 0', color: 'var(--primary-color)', fontSize: '0.9rem' }}>
                      {supportReplyAck}
                    </p>
                  )}
                  <div className="platform-modal-actions">
                    <button
                      type="button"
                      className="platform-btn platform-btn--ghost"
                      onClick={() => setSupportSelected(null)}
                    >
                      Close
                    </button>
                    <button type="submit" className="platform-btn" disabled={supportReplyBusy}>
                      {supportReplyBusy ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </section>
      )}

      {checklistCompany && (
        <div className="platform-modal-backdrop" role="presentation" onClick={() => setChecklistCompany(null)}>
          <div className="platform-modal" role="dialog" aria-modal="true" onClick={(ev) => ev.stopPropagation()}>
            <h3>Onboarding checklist</h3>
            <p style={{ color: 'var(--text-color-dark)', fontSize: '0.9rem' }}>
              Tenant: <strong style={{ color: 'var(--text-color)' }}>{checklistCompany.name}</strong> (id {checklistCompany.id}, slug {checklistCompany.slug || '—'})
            </p>
            <ol style={{ color: 'var(--text-color)', lineHeight: 1.7, paddingLeft: '1.2rem' }}>
              <li>Create tenant (platform)</li>
              <li>Invite first company admin (email + magic link backup)</li>
              <li>Company admin completes SSO in Settings → SSO</li>
              <li>Users sign in via SSO; company admin approves pending members in Settings → Team &amp; access</li>
              <li>Assign roles and scopes (job_ids, department_ids, org_wide) for least privilege</li>
            </ol>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-color-dark)' }}>
              SSO status (enriched list): {checklistCompany.sso_active ? 'integration marked active' : 'not active / not configured'}.
              Active members: {typeof checklistCompany.active_member_count === 'number' ? checklistCompany.active_member_count : '—'}.
            </p>
            <div className="platform-modal-actions">
              <button type="button" className="platform-btn" onClick={() => setChecklistCompany(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteCompany && (
        <div className="platform-modal-backdrop" role="presentation" onClick={closeInviteModal}>
          <div className="platform-modal" role="dialog" aria-modal="true" onClick={(ev) => ev.stopPropagation()}>
            <h3>Invite first company admin</h3>
            <p style={{ color: 'var(--text-color-dark)', fontSize: '0.9rem' }}>
              Tenant: <strong style={{ color: 'var(--text-color)' }}>{inviteCompany.name}</strong> (id {inviteCompany.id})
            </p>
            {!inviteResult && (
              <form onSubmit={submitFirstAdminInvite}>
                <label htmlFor="invite-email" style={{ display: 'block', marginBottom: '0.35rem', color: 'var(--text-color-dark)', fontSize: '0.85rem' }}>
                  Administrator email
                </label>
                <input
                  id="invite-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(ev) => setInviteEmail(ev.target.value)}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '10px', border: '1px solid var(--glass-border-color)', background: 'rgba(0,0,0,0.25)', color: 'var(--text-color)', fontFamily: 'inherit' }}
                />
                {inviteError && (
                  <div className="platform-alert platform-alert--error" style={{ marginTop: '0.75rem' }}>
                    {inviteError}
                  </div>
                )}
                <div className="platform-modal-actions">
                  <button type="button" className="platform-btn platform-btn--ghost" onClick={closeInviteModal}>
                    Cancel
                  </button>
                  <button type="submit" className="platform-btn" disabled={inviteBusy}>
                    {inviteBusy ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
              </form>
            )}
            {inviteResult?.already && (
              <>
                <p>This idempotency path was already processed for this company.</p>
                <div className="platform-modal-actions">
                  <button type="button" className="platform-btn" onClick={closeInviteModal}>
                    Close
                  </button>
                </div>
              </>
            )}
            {inviteResult && !inviteResult.already && (
              <>
                <div
                  className={`platform-alert ${
                    inviteResult.email_sent ? 'platform-alert--success' : 'platform-alert--error'
                  }`}
                >
                  {inviteResult.email_sent ? (
                    <>
                      Invitation email sent to <strong>{inviteResult.email}</strong> (user id{' '}
                      {inviteResult.user_id}). They should open the link in the email.
                    </>
                  ) : (
                    <>
                      Invite created for <strong>{inviteResult.email}</strong> (user id{' '}
                      {inviteResult.user_id}), but the email could not be sent. Share the magic link and
                      temporary password below manually.
                    </>
                  )}
                </div>
                {inviteResult.email_sent ? (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-color-dark)' }}>
                    A copy of the link and password is kept below only as a backup.
                  </p>
                ) : null}
                <p style={{ fontSize: '0.85rem', color: 'var(--text-color-dark)' }}>Magic link</p>
                <div className="platform-secret-block">{inviteResult.magic_link}</div>
                <button type="button" className="platform-btn" style={{ marginTop: '0.5rem' }} onClick={() => copyText('Magic link', inviteResult.magic_link)}>
                  Copy magic link
                </button>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-color-dark)', marginTop: '1rem' }}>Temporary password</p>
                <div className="platform-secret-block">{inviteResult.temporary_password}</div>
                <button type="button" className="platform-btn" style={{ marginTop: '0.5rem' }} onClick={() => copyText('Password', inviteResult.temporary_password)}>
                  Copy password
                </button>
                <div className="platform-modal-actions">
                  <button type="button" className="platform-btn" onClick={closeInviteModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {platformUserEdit && (
        <div className="platform-modal-backdrop" role="presentation" onClick={closePlatformUserEdit}>
          <div className="platform-modal" role="dialog" aria-modal="true" onClick={(ev) => ev.stopPropagation()}>
            <h3>Edit membership</h3>
            <p style={{ color: 'var(--text-color-dark)', fontSize: '0.9rem' }}>
              {platformUserEdit.email} · {platformUserEdit.company_name} (company id {platformUserEdit.company_id}, membership {platformUserEdit.membership_id})
            </p>
            <form onSubmit={savePlatformUserEdit}>
              <label className="team-gov__label" htmlFor="pf-role" style={{ marginTop: 8 }}>
                Role
              </label>
              <select
                id="pf-role"
                className="team-gov__select"
                value={platformUserEdit.roleEdit}
                onChange={(ev) => setPlatformUserEdit((p) => (p ? { ...p, roleEdit: ev.target.value } : p))}
              >
                {['company_admin', 'super_admin', 'hiring_manager', 'interviewer', 'guest'].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <label className="team-gov__label" htmlFor="pf-st" style={{ marginTop: 8 }}>
                Membership status
              </label>
              <select
                id="pf-st"
                className="team-gov__select"
                value={platformUserEdit.statusEdit}
                onChange={(ev) => setPlatformUserEdit((p) => (p ? { ...p, statusEdit: ev.target.value } : p))}
              >
                {['pending', 'active', 'suspended', 'rejected'].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <label className="team-gov__label" htmlFor="pf-scope" style={{ marginTop: 8 }}>
                Scope (JSON: job_ids, department_ids, org_wide)
              </label>
              <textarea
                id="pf-scope"
                className="team-gov__scope-ta"
                value={platformUserEdit.scopeText}
                onChange={(ev) => setPlatformUserEdit((p) => (p ? { ...p, scopeText: ev.target.value } : p))}
                rows={5}
                spellCheck={false}
                style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}
              />
              <div className="platform-modal-actions">
                <button type="button" className="platform-btn platform-btn--ghost" onClick={closePlatformUserEdit}>
                  Cancel
                </button>
                <button type="submit" className="platform-btn" disabled={platformUserSaving}>
                  {platformUserSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {platformUserDelete && (
        <div className="platform-modal-backdrop" role="presentation" onClick={closePlatformUserDelete}>
          <div className="platform-modal" role="dialog" aria-modal="true" onClick={(ev) => ev.stopPropagation()}>
            <h3>Delete user permanently</h3>
            <p style={{ color: 'var(--text-color-dark)', fontSize: '0.9rem' }}>
              {platformUserDelete.email} — {platformUserDelete.company_name} (user id {platformUserDelete.user_id}).
            </p>
            <p style={{ color: 'var(--text-color-dark)', fontSize: '0.85rem' }}>
              This removes the account and all job records they own. Type their email to confirm.
            </p>
            <form onSubmit={submitPlatformUserDelete}>
              <label className="team-gov__label" htmlFor="pf-del-email" style={{ marginTop: 8 }}>
                User email
              </label>
              <input
                id="pf-del-email"
                type="email"
                className="team-gov__select"
                style={{ width: '100%' }}
                autoComplete="off"
                value={platformUserDeleteEmail}
                onChange={(ev) => setPlatformUserDeleteEmail(ev.target.value)}
                placeholder="same as account email"
              />
              <div className="platform-modal-actions">
                <button
                  type="button"
                  className="platform-btn platform-btn--ghost"
                  onClick={closePlatformUserDelete}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="platform-btn platform-btn--danger"
                  disabled={platformUserDeleteBusy}
                >
                  {platformUserDeleteBusy ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/** Shown when membership is pending approval (SSO or invite). */
export function PendingMembershipPage() {
  return (
    <div className="governance-page">
      <h1>Access pending</h1>
      <p>Your account is waiting for a company administrator to approve access.</p>
    </div>
  );
}

/** Suspended or rejected company membership: no product access until an admin restores access. */
export function AccountInactivePage() {
  const onSignOut = () => {
    clearAuth();
    window.location.href = '/login';
  };
  return (
    <div className="governance-page">
      <h1>Access not available</h1>
      <p>
        Your account access has been removed. Please contact your company administrator to
        restore the access you need.
      </p>
      <p>
        <button type="button" className="platform-btn" onClick={onSignOut}>
          Sign out
        </button>
      </p>
    </div>
  );
}

/** Invite landing: set password and activate account from the emailed link. */
export function AcceptInvitePage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const [preview, setPreview] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    clearAuth();
    if (!token) {
      setLoadErr('Missing invitation token in the link.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const base = getBackendUrl();
        const r = await fetch(
          `${base}/api/auth/company-invite/preview?token=${encodeURIComponent(token)}`
        );
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setLoadErr(
            getUserFacingErrorMessage(r.status, data, data.detail || `Error ${r.status}`)
          );
          return;
        }
        setPreview(data);
        if (data.email) {
          const local = String(data.email).split('@')[0] || '';
          setName(local);
        }
      } catch (e) {
        if (!cancelled) setLoadErr(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setMsg('');
    if (password.length < 8) {
      setMsg('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setMsg('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      clearAuth();
      const base = getBackendUrl();
      const r = await fetch(`${base}/api/auth/company-invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          name: name.trim() || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg(getUserFacingErrorMessage(r.status, data, data.detail || `Error ${r.status}`));
        return;
      }
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setMsg('Account activated. Redirecting…');
      window.setTimeout(() => {
        window.location.href = '/jobs';
      }, 800);
    } catch (err) {
      setMsg(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="governance-page">
      <h1>Accept invitation</h1>
      {loadErr && <div className="platform-alert platform-alert--error">{loadErr}</div>}
      {!loadErr && !preview && token && <p className="page-lead">Loading invitation…</p>}
      {preview && (
        <>
          <p className="page-lead">
            You&apos;re invited to join{' '}
            <strong>{preview.company_name || 'the organization'}</strong>
            {preview.role ? (
              <>
                {' '}
                as <strong>{preview.role}</strong>
              </>
            ) : null}
            . Set a password to activate your account for <strong>{preview.email}</strong>.
          </p>
          <form className="platform-panel accept-invite-form" onSubmit={accept}>
            <label className="team-gov__label">
              <span>Email</span>
              <input type="email" value={preview.email} readOnly disabled />
            </label>
            <label className="team-gov__label">
              <span>Display name</span>
              <input
                type="text"
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                autoComplete="name"
              />
            </label>
            <label className="team-gov__label">
              <span>Password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
            </label>
            <label className="team-gov__label">
              <span>Confirm password</span>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
                autoComplete="new-password"
              />
            </label>
            <button type="submit" className="platform-btn" disabled={busy}>
              {busy ? 'Activating…' : 'Activate account'}
            </button>
            {msg && (
              <p
                className={
                  msg.startsWith('Account activated') ? '' : 'platform-alert platform-alert--error'
                }
              >
                {msg}
              </p>
            )}
          </form>
        </>
      )}
    </div>
  );
}
